import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendMessage, useToast } from 'shared/hooks';

import { SendMessageRequest } from '../types';

interface Options {
    onClose?: () => void;
}

export default function useOfferFeedbackDialog(messageType: SendMessageRequest['type'], { onClose }: Options = {}) {
    const { t } = useTranslation('feedback');
    const [ open, setOpen ] = useState(false);
    const [ orderId, setOrderId ] = useState<string>();
    const [ orderRoleId, setOrderRoleId ] = useState<string>();
    const { showSuccess } = useToast();
    
    const {
        mutate: sendFeedback,
        isPending: isLoading,
        error
    } = useSendMessage({
        onSuccess: () => showSuccess(t('generalDialog.sentSuccess'), 'feedbackSentSuccessfully')
    });

    const openDialog = useCallback((orderId: string, orderRoleId: string) => {
        setOrderId(orderId);
        setOrderRoleId(orderRoleId);
        setOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setOpen(false);
        setOrderId(undefined);
        setOrderRoleId(undefined);
        onClose?.();
    }, [ onClose ]);

    const handleSubmit = useCallback((data: SendMessageRequest['payload']) => {
        const request = {
            orderId,
            orderRoleId,
            type: messageType,
            payload: data
        } as SendMessageRequest;

        sendFeedback(request, {
            onSuccess: () => {
                closeDialog();
            }
        });
    }, [ orderId, orderRoleId, messageType, sendFeedback, closeDialog ]);

    return {
        open,
        orderId,
        orderRoleId,
        onOpen: openDialog,
        onClose: closeDialog,
        onSubmit: handleSubmit,
        isLoading,
        error
    };
}
